import Phaser from 'phaser';
import { NetworkService } from '../services/NetworkService';

/**
 * A utility class for monitoring and displaying network statistics in-game
 */
export class NetworkMonitor {
  private scene: Phaser.Scene;
  private networkService: NetworkService;
  private statsText: Phaser.GameObjects.Text;
  private enabled: boolean = false;
  private updateInterval: number = 1000; // Update every second
  private latencyHistory: number[] = [];
  private jitterValues: number[] = [];
  private packetsSent: number = 0;
  private packetsReceived: number = 0;
  private packetLossRate: number = 0;
  private lastPingTime: number = 0;
  private lastUpdateTime: number = 0;
  private lastLatency: number = 0;
  private maxHistoryLength: number = 100;
  private pingTimer: Phaser.Time.TimerEvent | null = null;
  
  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.networkService = NetworkService.getInstance();
    
    // Remove any existing network monitors in this scene
    this.removeExistingMonitors();
    
    // Create stats text display in the bottom-left corner
    this.statsText = this.scene.add.text(
      20, 
      this.scene.cameras.main.height - 120,
      'Network Stats\nPing: 0ms\nJitter: 0ms\nPacket Loss: 0%',
      { 
        font: '16px Arial',
        color: '#00ff00',
        backgroundColor: '#00000080', // Semi-transparent background
        padding: { x: 10, y: 10 },
        stroke: '#000000',
        strokeThickness: 1
      }
    ).setScrollFactor(0)  // Fixed position regardless of camera movement
     .setDepth(10000)     // Very high depth to ensure it's on top
     .setVisible(false);  // Initially hidden
    
    console.log(`Created Network Monitor at position: ${this.statsText.x}, ${this.statsText.y}`);
    
    // Make sure UI camera ignores this text element
    this.ensureProperRendering();
    
    // Add resize handler
    this.scene.scale.on('resize', this.handleResize, this);
    
    // Add update callback
    this.scene.events.on('update', this.update, this);
  }
  
  /**
   * Removes any existing network monitors in the scene
   */
  private removeExistingMonitors(): void {
    this.scene.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Text) {
        const text = child as Phaser.GameObjects.Text;
        if (text.text.includes('Network Stats')) {
          console.log('Removing existing Network Monitor');
          text.destroy();
        }
      }
    });
  }
  
  /**
   * Toggle the display of the network monitor
   */
  public toggleDisplay(show?: boolean): void {
    if (show !== undefined) {
      this.enabled = show;
    } else {
      this.enabled = !this.enabled;
    }
    
    this.statsText.setVisible(this.enabled);
    console.log(`Network monitor ${this.enabled ? 'enabled' : 'disabled'}`);
    
    // Ensure proper rendering with UI camera
    this.ensureProperRendering();
    
    if (this.enabled) {
      // Start ping interval when enabled
      this.setupPing();
    } else {
      // Stop ping interval when disabled
      this.stopPing();
    }
  }
  
  /**
   * Set up periodic ping measurements
   */
  private setupPing(): void {
    if (!this.pingTimer) {
      // Clear any existing ping measurements
      this.latencyHistory = [];
      this.jitterValues = [];
      this.packetsSent = 0;
      this.packetsReceived = 0;
      
      // Start measuring ping
      this.pingTimer = this.scene.time.addEvent({
        delay: 1000,
        callback: this.measureLatency,
        callbackScope: this,
        loop: true
      });
      
      console.log('Started ping interval');
      
      // Do an initial measurement
      this.measureLatency();
    }
  }
  
  /**
   * Stop ping measurements
   */
  private stopPing(): void {
    if (this.pingTimer) {
      this.pingTimer.remove();
      this.pingTimer = null;
      console.log('Stopped ping interval');
    }
  }
  
  /**
   * Measure current network latency using socket.io ping
   */
  private measureLatency(): void {
    if (!this.networkService.isConnected()) {
      this.updateStats(0, true);
      return;
    }
    
    try {
      // Record ping start time
      this.lastPingTime = Date.now();
      this.packetsSent++;
      
      // Use socket.io's built-in ping method
      const socket = this.networkService.getSocket();
      if (socket) {
        socket.emit('ping', () => {
          // Calculate latency
          const now = Date.now();
          const latency = now - this.lastPingTime;
          this.packetsReceived++;
          
          // Update statistics
          this.updateStats(latency);
        });
      } else {
        this.updateStats(0, true);
      }
      
      // Add timeout to detect packet loss
      setTimeout(() => {
        if (this.lastPingTime > 0 && Date.now() - this.lastPingTime > 3000) {
          // If no response after 3 seconds, consider it packet loss
          this.lastPingTime = 0;
          this.updateStats(0, true);
        }
      }, 3000);
    } catch (error) {
      console.error('Error measuring latency:', error);
    }
  }
  
  /**
   * Update network statistics
   */
  private updateStats(latency: number, isLoss: boolean = false): void {
    // Handle packet loss
    if (isLoss) {
      // Don't add to history, just update loss rate
      if (this.packetsSent > 0) {
        this.packetLossRate = 1 - (this.packetsReceived / this.packetsSent);
      }
      return;
    }
    
    // Add to latency history
    this.latencyHistory.push(latency);
    if (this.latencyHistory.length > this.maxHistoryLength) {
      this.latencyHistory.shift();
    }
    
    // Calculate jitter (variation between consecutive latency measurements)
    if (this.lastLatency > 0) {
      const jitter = Math.abs(latency - this.lastLatency);
      this.jitterValues.push(jitter);
      if (this.jitterValues.length > this.maxHistoryLength) {
        this.jitterValues.shift();
      }
    }
    
    // Update last latency value
    this.lastLatency = latency;
    
    // Update packet loss rate
    if (this.packetsSent > 0) {
      this.packetLossRate = 1 - (this.packetsReceived / this.packetsSent);
    }
  }
  
  /**
   * Update the display periodically
   */
  update(time: number): void {
    if (!this.enabled) return;
    
    // Only update the display periodically to avoid too many updates
    if (time < this.lastUpdateTime + this.updateInterval) return;
    this.lastUpdateTime = time;
    
    // Calculate average values
    const avgLatency = this.getAverageLatency();
    const avgJitter = this.getAverageJitter();
    const lossPercent = this.packetLossRate * 100;
    
    // Update color based on latency
    let color = '#00ff00'; // Green for good connection
    if (avgLatency > 150 || lossPercent > 5) {
      color = '#ff0000'; // Red for bad connection
    } else if (avgLatency > 80 || lossPercent > 1) {
      color = '#ffff00'; // Yellow for average connection
    }
    
    // Format the stats text
    const socketId = this.networkService.getSocketId();
    const idText = socketId ? `ID: ${socketId.substring(0, 8)}...` : 'Not connected';
    const statsText = [
      'Network Stats',
      idText,
      `Ping: ${avgLatency.toFixed(1)}ms`,
      `Jitter: ${avgJitter.toFixed(1)}ms`,
      `Packet Loss: ${lossPercent.toFixed(1)}%`,
      `Sent/Received: ${this.packetsSent}/${this.packetsReceived}`
    ].join('\n');
    
    this.statsText.setText(statsText);
    this.statsText.setColor(color);
  }
  
  /**
   * Get average latency from history
   */
  private getAverageLatency(): number {
    if (this.latencyHistory.length === 0) return 0;
    return this.latencyHistory.reduce((sum, val) => sum + val, 0) / this.latencyHistory.length;
  }
  
  /**
   * Get average jitter from history
   */
  private getAverageJitter(): number {
    if (this.jitterValues.length === 0) return 0;
    return this.jitterValues.reduce((sum, val) => sum + val, 0) / this.jitterValues.length;
  }
  
  /**
   * Handle window resize
   */
  private handleResize(): void {
    // Reposition the stats display
    this.statsText.setPosition(20, this.scene.cameras.main.height - 120);
  }
  
  /**
   * Check if the network monitor is currently visible
   */
  public isVisible(): boolean {
    return this.enabled;
  }
  
  /**
   * Clean up resources
   */
  public destroy(): void {
    // Stop the ping interval
    this.stopPing();
    
    // Remove event listeners
    this.scene.events.off('update', this.update, this);
    this.scene.scale.off('resize', this.handleResize, this);
    
    // Destroy the text object
    if (this.statsText) {
      this.statsText.destroy();
    }
    
    console.log('Network monitor destroyed');
  }
  
  /**
   * Ensure the network monitor is properly rendered by the main camera only
   * and ignored by the UI camera to prevent double rendering
   */
  public ensureProperRendering(): void {
    // Get the UI camera
    const uiCam = this.scene.cameras.getCamera('uiCam');
    if (uiCam && this.statsText) {
      // Make sure UI camera ignores this text element
      uiCam.ignore(this.statsText);
      console.log('Network monitor configured to be ignored by UI camera');
    }
  }
} 